import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FileInput, ScanSearch, ListChecks, ShieldCheck, UserCheck, Download, GraduationCap,
  ArrowRight, Save, Settings2,
} from 'lucide-react';
import settingsService from '../../services/settingsService';
import { PageHeader, Spinner, InfoHint, Button } from '../../components/ui';
import '../../styles/theme.css';

type Field =
  | { key: string; label: string; type: 'text' | 'number' | 'textarea'; hint?: string; placeholder?: string }
  | { key: string; label: string; type: 'select'; options: string[]; hint?: string }
  | { key: string; label: string; type: 'bool'; hint?: string };

interface Stage {
  key: string;
  title: string;
  icon: React.ReactNode;
  blurb: string;
  links?: { to: string; label: string; hint: string }[];
  fields?: Field[];
}

// Each pipeline stage maps to a config surface. Some stages own a full existing page (extract,
// verify, training) and we link there; the rest persist their settings here via the settings API.
const STAGES: Record<string, Stage> = {
  ingest: {
    key: 'ingest', title: 'Ingest', icon: <FileInput size={18} />,
    blurb: 'How documents are read and stored before analysis.',
    fields: [
      { key: 'INGEST_DEFAULT_SENSITIVITY', label: 'Default sensitivity', type: 'select',
        options: ['confidential', 'public'],
        hint: 'New uploads default to this. Confidential documents are never sent to public AI services.' },
      { key: 'INGEST_MAX_PAGES', label: 'Max pages to read', type: 'number', placeholder: '500',
        hint: 'Documents longer than this are truncated for analysis.' },
    ],
  },
  classify: {
    key: 'classify', title: 'Classify', icon: <ScanSearch size={18} />,
    blurb: 'Deciding whether an uploaded file is a loan or credit agreement.',
    fields: [
      { key: 'CLASSIFY_MIN_CONFIDENCE', label: 'Minimum confidence (0–1)', type: 'number', placeholder: '0.6',
        hint: 'Below this, a document is set aside as “not a loan agreement” instead of analyzed.' },
    ],
  },
  extract: {
    key: 'extract', title: 'Extract', icon: <Settings2 size={18} />,
    blurb: 'The terms DocShawk looks for, the models that read the document, and the agent.',
    links: [
      { to: '/admin/parameters', label: 'Terms & patterns', hint: 'The fields to extract and their matching rules.' },
      { to: '/admin/settings', label: 'Models & keys', hint: 'The open-source and frontier models used to read documents.' },
    ],
    fields: [
      { key: 'AGENT_EXTRACTION_ENABLED', label: 'Agentic extraction', type: 'bool',
        hint: 'Run an AI agent that searches the document with tools and self-verifies each term. It competes as an extra method — the best-grounded value still wins. Public documents use a frontier model; confidential documents stay on the local model (weaker).' },
      { key: 'AGENT_MAX_ITERATIONS', label: 'Max agent steps', type: 'number', placeholder: '10',
        hint: 'How many tool rounds the agent may take per document before it must stop.' },
      { key: 'AGENT_SYSTEM_PROMPT', label: 'Agent instructions (system prompt)', type: 'textarea',
        placeholder: 'Leave blank to use the default extraction instructions.',
        hint: 'Customize how the agent behaves — its role, priorities, and rules. Blank = the built-in default.' },
    ],
  },
  verify: {
    key: 'verify', title: 'Verify', icon: <ListChecks size={18} />,
    blurb: 'The checks every extracted value must pass, and which ones block export.',
    links: [{ to: '/admin/evals', label: 'Checks & gates', hint: 'Grounding, type, range, and cross-field checks — and which are required.' }],
  },
  review: {
    key: 'review', title: 'Review', icon: <UserCheck size={18} />,
    blurb: 'The human approval gate before a document can be exported.',
    fields: [
      { key: 'REVIEW_ALLOW_OVERRIDE', label: 'Allow “finish anyway” override', type: 'bool',
        hint: 'Let reviewers export past a failing required check. The override is recorded in the export.' },
    ],
  },
  export: {
    key: 'export', title: 'Export', icon: <Download size={18} />,
    blurb: 'The output produced when a document is finished.',
    fields: [
      { key: 'EXPORT_INCLUDE_CITATIONS', label: 'Include page + snippet citations', type: 'bool',
        hint: 'Every exported row carries the page and exact passage each value came from.' },
    ],
  },
  training: {
    key: 'training', title: 'LLM training', icon: <GraduationCap size={18} />,
    blurb: 'Fine-tuning the open-source model on confirmed data, promotion, and automation.',
    links: [{ to: '/admin/training', label: 'Training & automation', hint: 'Datasets, fine-tune jobs, eval-gated promotion, and the auto-train switches.' }],
  },
};

const ORDER = ['ingest', 'classify', 'extract', 'verify', 'review', 'export', 'training'];

const StageConfig: React.FC = () => {
  const { stage } = useParams();
  const navigate = useNavigate();
  const cfg = stage ? STAGES[stage] : undefined;

  const [values, setValues] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    if (!cfg?.fields) { setLoading(false); return; }
    setLoading(true);
    try {
      const { status } = await settingsService.getProviders();
      const v: Record<string, string> = {};
      cfg.fields.forEach((f) => { v[f.key] = status[f.key]?.preview ?? ''; });
      setValues(v);
    } finally { setLoading(false); }
  }, [cfg]);

  useEffect(() => { load(); }, [load]);

  // ---- index (no stage) ----
  if (!stage) {
    return (
      <div>
        <PageHeader title="Pipeline configuration"
          subtitle="Each stage of the pipeline is configurable. Pick a stage to set it up." />
        <div className="mx-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
          {ORDER.map((k) => {
            const s = STAGES[k];
            return (
              <button key={k} className="mx-card mx-card-pad" style={{ textAlign: 'left', cursor: 'pointer' }}
                onClick={() => navigate(`/admin/config/${k}`)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, color: 'var(--mx-accent)' }}>
                  {s.icon}<span style={{ fontWeight: 600, color: 'var(--mx-text)' }}>{s.title}</span></div>
                <div className="mx-muted" style={{ fontSize: 12.5, marginTop: 6 }}>{s.blurb}</div>
                <div style={{ color: 'var(--mx-accent)', fontSize: 12, marginTop: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                  Configure <ArrowRight size={13} /></div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (!cfg) {
    return (
      <div>
        <PageHeader title="Unknown stage" />
        <div className="mx-card mx-card-pad">
          <div className="mx-muted">No pipeline stage called “{stage}”.</div>
          <Button icon={Settings2} variant="primary" size="sm" style={{ marginTop: 12 }}
            onClick={() => navigate('/admin/config')}>All stages</Button>
        </div>
      </div>
    );
  }

  const save = async () => {
    if (!Object.keys(dirty).length) { setMsg('Nothing changed.'); return; }
    setSaving(true); setMsg('');
    try {
      const { status } = await settingsService.setProviders(dirty);
      const v = { ...values };
      Object.keys(dirty).forEach((k) => { v[k] = status[k]?.preview ?? dirty[k]; });
      setValues(v); setDirty({});
      setMsg('Saved.');
    } catch (e: any) { setMsg(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const set = (k: string, val: string) => {
    setValues((v) => ({ ...v, [k]: val }));
    setDirty((d) => ({ ...d, [k]: val }));
  };

  return (
    <div>
      <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Link to="/admin/config" className="mx-subtle" style={{ fontSize: 12.5, textDecoration: 'none' }}>← Pipeline configuration</Link>
        <span style={{ color: 'var(--mx-accent)', display: 'inline-flex' }}>{cfg.icon}</span>
      </div>
      <PageHeader title={cfg.title} subtitle={cfg.blurb} />

      {cfg.links && (
        <div className="mx-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
          {cfg.links.map((l) => (
            <button key={l.to} className="mx-card mx-card-pad" style={{ textAlign: 'left', cursor: 'pointer' }}
              onClick={() => navigate(l.to)}>
              <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--mx-accent)' }}>
                {l.label} <ArrowRight size={14} /></div>
              <div className="mx-muted" style={{ fontSize: 12.5, marginTop: 5 }}>{l.hint}</div>
            </button>
          ))}
        </div>
      )}

      {cfg.fields && (loading ? <div className="mx-card mx-card-pad"><Spinner label="Loading…" /></div> : (
        <div className="mx-card mx-card-pad" style={{ maxWidth: 560 }}>
          {msg && <div className="mx-muted" style={{ fontSize: 13, marginBottom: 12 }}>{msg}</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {cfg.fields.map((f) => (
              <div key={f.key}>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {f.label}{f.hint && <InfoHint text={f.hint} />}</label>
                <div style={{ marginTop: 6 }}>
                  {f.type === 'bool' ? (
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                      <input type="checkbox" checked={values[f.key] === '1' || values[f.key] === 'true'}
                        onChange={(e) => set(f.key, e.target.checked ? '1' : '0')} />
                      {values[f.key] === '1' || values[f.key] === 'true' ? 'Enabled' : 'Disabled'}
                    </label>
                  ) : f.type === 'select' ? (
                    <select className="mx-select" value={values[f.key] || ''} onChange={(e) => set(f.key, e.target.value)}>
                      <option value="">— default —</option>
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : f.type === 'textarea' ? (
                    <textarea className="mx-input" rows={6} style={{ fontFamily: 'inherit', resize: 'vertical' }}
                      placeholder={('placeholder' in f && f.placeholder) || ''}
                      value={values[f.key] || ''} onChange={(e) => set(f.key, e.target.value)} />
                  ) : (
                    <input className="mx-input" type={f.type} placeholder={('placeholder' in f && f.placeholder) || ''}
                      value={values[f.key] || ''} onChange={(e) => set(f.key, e.target.value)} />
                  )}
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 20 }}>
            <Button icon={Save} variant="primary" onClick={save} disabled={saving || !Object.keys(dirty).length}>
              {saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </div>
      ))}

      <div className="mx-card mx-card-pad mx-subtle" style={{ fontSize: 11.5, marginTop: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
        <ShieldCheck size={14} /> Settings are stored per firm and take effect on the next run.
      </div>
    </div>
  );
};

export default StageConfig;
